import type { WidgetProps } from '@rjsf/utils'
import { Mtds, PlusIcon } from 'prove-common'
import { useEffect, useMemo, useState } from 'react'
import { CreateMatrixModal } from '../../create-matrix-modal/CreateMatrixModal'

export const SelectMatrixWidget = ({
  disabled,
  id,
  onChange,
  options: { enumOptions: options },
  placeholder,
  required,
  value
}: WidgetProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  // When a matrix is created, store it here and wait until options update.
  const [pendingCreated, setPendingCreated] = useState<{
    id: number
    code?: string
  } | null>(null)
  const selectedValues = Array.isArray(value)
    ? new Set(value.map(String))
    : null
  const selected = options
    ?.filter((o) =>
      selectedValues
        ? selectedValues.has(String(o.value))
        : `${o.value}` === `${value}`
    )
    .map(({ label, value }) => ({ label, value: String(value) })) // Convert value to string for HTML compatibility

  const isAnySelectionNewCombination = options?.some(({ schema }) => {
    return selected?.some(
      (sel) =>
        sel.value === `${schema?.const}` &&
        schema?.['x-new-combination'] === true
    )
  })

  const { regularOptions, newCombinationOptions } = useMemo(() => {
    const regular = (options ?? []).filter(
      ({ schema }) => schema?.['x-new-combination'] !== true
    )
    const newCombos = (options ?? []).filter(
      ({ schema }) => schema?.['x-new-combination'] === true
    )
    return { regularOptions: regular, newCombinationOptions: newCombos }
  }, [options])

  const handleSelectedChange = (item: { label: string; value: string }) => {
    onChange(options?.find((o) => `${o.value}` === `${item?.value}`)?.value)
  }

  const handleCreatedSelect = (created: { id: number; code?: string }) => {
    // Defer selection until options are updated by query cache refetch.
    setPendingCreated(created)
  }

  const openCreateDialog = () => {
    ;(document.activeElement as HTMLElement | null)?.blur()
    setIsDialogOpen(true)
  }

  // Once options include the newly created item, select it and clear pending state.
  useEffect(() => {
    if (!pendingCreated) return
    const match = options?.find((o) => o.value === pendingCreated.id)
    if (!match) return
    const nextSelected = { label: match.label, value: `${match.value}` }
    handleSelectedChange(nextSelected)
    setIsDialogOpen(false)
    setPendingCreated(null)
  }, [options, pendingCreated])

  return (
    <>
      <Mtds.Field.Suggestion
        data-color={isAnySelectionNewCombination ? 'info' : undefined}
        selected={selected}
        onSelectedChange={([item]) => handleSelectedChange(item)}
      >
        <Mtds.Input
          disabled={disabled}
          id={id}
          placeholder={placeholder}
          required={required}
        />
        <button type='reset' aria-label='Fjern tekst' />
        <Mtds.Field.Datalist>
          {regularOptions.map(({ label, value }) => (
            <Mtds.Field.Option key={value} value={value}>
              {label}
            </Mtds.Field.Option>
          ))}

          {regularOptions.length > 0 && newCombinationOptions.length > 0 && (
            <div role='separator'>
              <Mtds.Divider data-gap={0} />
            </div>
          )}

          {newCombinationOptions.map(({ label, value }) => {
            return (
              <Mtds.Field.Option key={value} value={value} data-color={'info'}>
                <Mtds.Flex data-justify='space-between'>
                  {label}
                  <Mtds.Tag data-color={'info'} data-size='sm'>
                    Ny kombinasjon
                  </Mtds.Tag>
                </Mtds.Flex>
              </Mtds.Field.Option>
            )
          })}

          <div role='separator'>
            <Mtds.Divider data-gap={0} />
          </div>

          <Mtds.Button
            className='w-full'
            data-variant='tertiary'
            onMouseDown={(event) => event.preventDefault()}
            onClick={openCreateDialog}
          >
            <PlusIcon />
            Opprett ny
          </Mtds.Button>
        </Mtds.Field.Datalist>
      </Mtds.Field.Suggestion>

      {/* Modal for creating a new entry */}
      {isDialogOpen && (
        <CreateMatrixModal
          isOpen={isDialogOpen}
          setOpen={setIsDialogOpen}
          onCreated={handleCreatedSelect}
        />
      )}
    </>
  )
}
